import React, { useState } from "react";
import { VoiceRecorder } from "./VoiceRecorder";
import { useVideo } from "../contexts/VideoContext";
import { useAuth0 } from "@auth0/auth0-react";
import { Wand2, Upload, Palette } from "lucide-react";

const videoFilters = [
  { name: "Brak", value: "none" },
  { name: "Sepia", value: "sepia(60%)" },
  { name: "Czarno-biały", value: "grayscale(100%)" },
  { name: "Kontrast", value: "contrast(150%)" },
  { name: "Neon", value: "hue-rotate(270deg) saturate(200%)" },
  { name: "Vintage", value: "sepia(30%) contrast(120%) brightness(110%)" },
  { name: "Cool", value: "hue-rotate(180deg) saturate(150%)" }
];

export const VideoGenerator: React.FC = () => {
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStep, setGenerationStep] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("none");
  const [voiceBlob, setVoiceBlob] = useState<Blob | null>(null);
  const { addVideo } = useVideo();
  const { user } = useAuth0();

  const generateVideo = async () => {
    if (!prompt.trim()) return;
    
    setIsGenerating(true);
    setGenerationStep("Generuję muzykę...");

    try {
      let audioUrl = "";
      
      if (voiceBlob) {
        setGenerationStep("Przetwarzam nagrany głos...");
        const formData = new FormData();
        formData.append("audio", voiceBlob);
        
        const transcribeResponse = await fetch("/api/transcribe", {
          method: "POST",
          body: formData
        });
        const transcriptionData = await transcribeResponse.json();
        audioUrl = URL.createObjectURL(voiceBlob);
      } else {
        setGenerationStep("Generuję muzykę AI...");
        const musicResponse = await fetch("/api/generate-music", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ prompt })
        });
        const musicData = await musicResponse.json();
        audioUrl = musicData.audioUrl;
      }

      setGenerationStep("Tworzę wizualizacje...");
      const imageResponse = await fetch("/api/generate-images", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt, count: 30 })
      });
      const imageData = await imageResponse.json();

      const newVideo = {
        title: generateTitle(prompt),
        artist: user?.name || "AI Artist",
        audioUrl,
        frames: imageData.frames,
        thumbnail: imageData.frames[0],
        duration: 180,
        userId: user?.sub || "anonymous",
        tags: extractTags(prompt)
      };

      addVideo(newVideo);
      setGenerationStep("Gotowe!");
      
      // Clear form
      setPrompt("");
      setVoiceBlob(null);
      
    } catch (error) {
      console.error("Generation error:", error);
      setGenerationStep("Błąd podczas generowania");
    } finally {
      setIsGenerating(false);
      setTimeout(() => setGenerationStep(""), 3000);
    }
  };

  const generateTitle = (prompt: string): string => {
    const templates = [
      `${prompt} - AI Generated`,
      `Original: ${prompt}`,
      `${prompt} (AI Music Video)`,
      `${prompt} - Electronic Mix`
    ];
    return templates[Math.floor(Math.random() * templates.length)];
  };

  const extractTags = (prompt: string): string[] => {
    const commonTags = ["ai", "generated", "music", "video"];
    const promptWords = prompt.toLowerCase().split(" ").filter(word => word.length > 3);
    return [...commonTags, ...promptWords.slice(0, 5)];
  };

  return (
    <div className="video-generator">
      <div className="generator-header">
        <Wand2 size={24} />
        <h2>Stwórz AI Video</h2>
      </div>

      {generationStep && (
        <div className="generation-status">
          <div className="status-text">{generationStep}</div>
          <div className="progress-bar">
            <div className="progress-fill"></div>
          </div>
        </div>
      )}

      <div className="generator-form">
        <div className="input-group">
          <label>Opisz swoje video:</label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="np. 'energetyczna muzyka elektroniczna z neonowymi wizualizacjami'"
            disabled={isGenerating}
            rows={3}
          />
        </div>

        <div className="input-group">
          <label>Filtr wideo:</label>
          <select 
            value={selectedFilter} 
            onChange={(e) => setSelectedFilter(e.target.value)}
            disabled={isGenerating}
          >
            {videoFilters.map(filter => (
              <option key={filter.value} value={filter.value}>
                {filter.name}
              </option>
            ))}
          </select>
        </div>

        <div className="input-group">
          <label>Lub nagraj własny głos:</label>
          <VoiceRecorder onRecordingComplete={setVoiceBlob} />
        </div>

        <button 
          onClick={generateVideo}
          disabled={isGenerating || (!prompt.trim() && !voiceBlob)}
          className="generate-button"
        >
          <Wand2 size={20} />
          {isGenerating ? "Generuję..." : "Generuj Video"}
        </button>
      </div>
    </div>
  );
};
